/**
 * Agent Metrics — scorecard with 4 metric categories
 *
 * Route: /admin/reporting/agents/:id (index)
 */

import { useState, useMemo, useCallback } from 'react'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { DatePicker } from '@/common/components/date-picker'
import { useAgentMetrics } from '@/features/reporting/hooks/useReportingData'
import { semantic } from '@/features/reporting/chartPalette'
import * as styles from '../../index.css'

export const Route = createFileRoute({
  component: AgentMetricsPage,
})

// =============================================================================
// Types
// =============================================================================

type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'

interface MetricCardData {
  label: string
  value: string
  delta?: number | null
  comingSoon?: boolean
}

// =============================================================================
// Formatting Helpers
// =============================================================================

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

function formatPct(value: number | null): string {
  if (value === null) return 'N/A'
  return `${value.toFixed(1)}%`
}

function formatDays(value: number | null): string {
  if (value === null) return 'N/A'
  return `${value.toFixed(1)} days`
}

function formatCurrency(value: number | null): string {
  if (value === null) return 'N/A'
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function formatDecimal(value: number | null): string {
  if (value === null) return 'N/A'
  return value.toFixed(1)
}

function calcDelta(current: number, prior: number): number | null {
  if (prior === 0) return null
  return ((current - prior) / prior) * 100
}

function deltaColor(delta: number | null): string {
  if (delta === null) return semantic.neutral
  if (delta > 0) return semantic.positive
  if (delta < 0) return semantic.negative
  return semantic.neutral
}

function deltaLabel(delta: number | null): string {
  if (delta === null) return 'N/A'
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

function deltaIndicator(delta: number | null): string {
  if (delta === null) return ''
  if (delta > 0) return '\u25B2 '
  if (delta < 0) return '\u25BC '
  return ''
}

// =============================================================================
// Metric Card Component
// =============================================================================

function MetricCard({ label, value, delta, comingSoon }: MetricCardData) {
  return (
    <div className={styles.metricCard} style={{ cursor: 'default' }}>
      <Text variant="bodySm" color="muted">{label}</Text>
      {comingSoon ? (
        <div style={{ margin: '4px 0' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '12px',
            backgroundColor: '#f0f0f0',
            color: '#888',
            fontSize: '13px',
            fontWeight: 500,
          }}>
            Coming Soon
          </span>
        </div>
      ) : (
        <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>{value}</Text>
      )}
      {delta !== undefined && !comingSoon && (
        <Text variant="bodyXs" style={{ color: deltaColor(delta ?? null) }}>
          {deltaIndicator(delta ?? null)}
          {deltaLabel(delta ?? null)} vs prior period
        </Text>
      )}
    </div>
  )
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <Text variant="body" weight="bold">{title}</Text>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function AgentMetricsPage() {
  const { id } = Route.useParams()
  const [dateRange, setDateRange] = useState<DateRangePreset>('1y')
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 12), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  const { data: metrics, isLoading: metricsLoading } = useAgentMetrics(id, startDateStr, endDateStr)

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange])

  // Build metric cards
  const submissionCards: MetricCardData[] = [
    { label: 'Properties Submitted', value: formatCount(metrics?.submissions.properties_submitted ?? 0), delta: metrics ? calcDelta(metrics.submissions.properties_submitted, metrics.submissions.properties_submitted_prior) : null },
    { label: 'Avg. Submissions/Month', value: formatDecimal(metrics?.submissions.avg_submissions_per_month ?? null) },
    { label: 'Time to First Submission', value: formatDays(metrics?.submissions.time_to_first_submission_days ?? null) },
    { label: 'Questions Received', value: formatCount(metrics?.submissions.total_questions_received ?? 0), delta: metrics ? calcDelta(metrics.submissions.total_questions_received, metrics.submissions.total_questions_received_prior) : null },
    { label: 'Offers Received', value: formatCount(metrics?.submissions.total_offers_received ?? 0), delta: metrics ? calcDelta(metrics.submissions.total_offers_received, metrics.submissions.total_offers_received_prior) : null },
  ]

  const engagementCards: MetricCardData[] = [
    { label: 'Total Logins', value: '', comingSoon: true },
    { label: 'Login Frequency', value: '', comingSoon: true },
    { label: 'Last Login', value: metrics?.engagement.last_login_at ? formatRelativeTime(metrics.engagement.last_login_at) : 'Never' },
    { label: 'Days Since Last Login', value: metrics?.engagement.days_since_last_login != null ? String(metrics.engagement.days_since_last_login) : 'N/A' },
  ]

  const offerResponsivenessCards: MetricCardData[] = [
    { label: 'Avg. Time to First Offer', value: formatDays(metrics?.offer_responsiveness.avg_time_to_first_offer_days ?? null) },
    { label: 'Offer Rate', value: formatPct(metrics?.offer_responsiveness.offer_rate ?? null) },
    { label: 'Avg. Offers per Property', value: formatDecimal(metrics?.offer_responsiveness.avg_offers_per_property ?? null) },
  ]

  const conversionCards: MetricCardData[] = [
    { label: 'Gone UC', value: formatCount(metrics?.conversion.gone_uc ?? 0), delta: metrics ? calcDelta(metrics.conversion.gone_uc, metrics.conversion.gone_uc_prior) : null },
    { label: 'Closed', value: formatCount(metrics?.conversion.closed ?? 0), delta: metrics ? calcDelta(metrics.conversion.closed, metrics.conversion.closed_prior) : null },
    { label: 'UC\u2192Close Rate', value: formatPct(metrics?.conversion.uc_to_close_rate ?? null) },
    { label: 'Avg. Days to Close', value: formatDays(metrics?.conversion.avg_days_to_close ?? null) },
    { label: 'Active Listings', value: formatCount(metrics?.conversion.active_listings ?? 0) },
    { label: 'Pipeline Value', value: formatCurrency(metrics?.conversion.pipeline_value ?? null) },
  ]

  return (
    <>
      {/* Time Period Filter */}
      <div className={styles.controls}>
        <div className={styles.controlDateGroup}>
          <div className={styles.granularityToggle}>
            {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
              <button
                key={preset}
                className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)}
                onClick={() => handleDateRangeChange(preset)}
              >
                {preset === 'custom' ? 'Custom' : preset}
              </button>
            ))}
          </div>
          <div className={styles.customDateRange}>
            <div className={styles.customDatePicker}>
              <DatePicker label="Start" value={new Date(startDateStr)} onChange={setCustomStart} maxDate={new Date(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
            </div>
            <Text variant="bodySm" color="muted">{'\u2013'}</Text>
            <div className={styles.customDatePicker}>
              <DatePicker label="End" value={new Date(endDateStr)} onChange={setCustomEnd} minDate={new Date(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
            </div>
          </div>
        </div>
      </div>

      {metricsLoading && (
        <Stack gap="16" align="center" justify="center" style={{ minHeight: '200px' }}>
          <LoadingSpinner size="sm" />
        </Stack>
      )}

      {!metricsLoading && (
        <>
          <SectionHeader title="Submission Activity" />
          <div className={styles.metricsGrid}>
            {submissionCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <SectionHeader title="Engagement" />
          <div className={styles.metricsGrid}>
            {engagementCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <SectionHeader title="Offer Responsiveness" />
          <div className={styles.metricsGrid}>
            {offerResponsivenessCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>

          <SectionHeader title="Conversion & Pipeline" />
          <div className={styles.metricsGrid}>
            {conversionCards.map((card) => <MetricCard key={card.label} {...card} />)}
          </div>
        </>
      )}
    </>
  )
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return `${Math.floor(diffDays / 365)} years ago`
}
